import { motion } from "framer-motion";
import SEO from "../components/SEO";

export default function Terms() {
  return (
    <>
      <SEO 
        title="Terms of Service | AI Film Society"
        description="Terms of Service for AI Film Society - Read our terms and conditions for using our platform."
      />
      
      <div className="min-h-screen py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="font-syne font-bold text-4xl sm:text-5xl text-white mb-4">
              Terms of Service
            </h1>
            <p className="text-slate-400 mb-12">
              Last updated: January 24, 2026
            </p>

            <div className="prose prose-invert prose-purple max-w-none space-y-8">
              <section>
                <h2 className="font-syne text-2xl text-white mb-4">1. Acceptance of Terms</h2>
                <p className="text-slate-300 leading-relaxed">
                  By accessing or using the AI Film Society website and services, you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our services.
                </p>
              </section>

              <section>
                <h2 className="font-syne text-2xl text-white mb-4">2. Description of Service</h2>
                <p className="text-slate-300 leading-relaxed">
                  AI Film Society is a community platform for AI filmmakers, enthusiasts, and industry professionals. Our services include:
                </p>
                <ul className="list-disc list-inside text-slate-300 mt-4 space-y-2">
                  <li>Access to AI film news, events, and resources</li>
                  <li>Film submission and showcase opportunities</li>
                  <li>Community forums and member directory</li>
                  <li>Job board for industry opportunities</li>
                  <li>Educational workshops and content</li>
                </ul>
              </section>

              <section>
                <h2 className="font-syne text-2xl text-white mb-4">3. User Accounts</h2>
                <p className="text-slate-300 leading-relaxed mb-4">
                  To access certain features, you must create an account. You agree to:
                </p>
                <ul className="list-disc list-inside text-slate-300 space-y-2">
                  <li>Provide accurate and complete information</li>
                  <li>Maintain the security of your account credentials</li>
                  <li>Notify us immediately of any unauthorized access</li>
                  <li>Accept responsibility for all activities under your account</li>
                </ul>
              </section>

              <section>
                <h2 className="font-syne text-2xl text-white mb-4">4. User Content</h2>
                <p className="text-slate-300 leading-relaxed mb-4">
                  You retain ownership of content you submit. By posting content, you grant AI Film Society a non-exclusive, worldwide, royalty-free license to use, display, and distribute your content in connection with our services.
                </p>
                <p className="text-slate-300 leading-relaxed">
                  You agree not to post content that:
                </p>
                <ul className="list-disc list-inside text-slate-300 mt-4 space-y-2">
                  <li>Infringes on intellectual property rights</li>
                  <li>Is unlawful, harmful, or offensive</li>
                  <li>Contains malware or harmful code</li>
                  <li>Misrepresents your identity or affiliation</li>
                  <li>Violates any applicable laws or regulations</li>
                </ul>
              </section>

              <section>
                <h2 className="font-syne text-2xl text-white mb-4">5. Film Submissions</h2>
                <p className="text-slate-300 leading-relaxed">
                  When submitting films for consideration, you represent that you have all necessary rights to the content. All submissions are subject to review and approval by our team. We reserve the right to reject or remove submissions at our discretion.
                </p>
              </section>

              <section>
                <h2 className="font-syne text-2xl text-white mb-4">6. Job Postings</h2>
                <p className="text-slate-300 leading-relaxed">
                  Job postings are subject to approval. Employers must provide accurate job information and comply with applicable employment laws. AI Film Society is not responsible for the accuracy of job listings or hiring decisions.
                </p>
              </section>

              <section>
                <h2 className="font-syne text-2xl text-white mb-4">7. Donations and Payments</h2>
                <p className="text-slate-300 leading-relaxed">
                  Donations are voluntary and non-refundable unless required by law. Payment processing is handled by Stripe, and you agree to their terms of service when making payments.
                </p>
              </section>

              <section>
                <h2 className="font-syne text-2xl text-white mb-4">8. Intellectual Property</h2>
                <p className="text-slate-300 leading-relaxed">
                  The AI Film Society name, logo, and website design are our intellectual property. You may not use our branding without written permission.
                </p>
              </section>

              <section>
                <h2 className="font-syne text-2xl text-white mb-4">9. Disclaimer of Warranties</h2>
                <p className="text-slate-300 leading-relaxed">
                  Our services are provided "as is" without warranties of any kind. We do not guarantee uninterrupted or error-free service.
                </p>
              </section>

              <section>
                <h2 className="font-syne text-2xl text-white mb-4">10. Limitation of Liability</h2>
                <p className="text-slate-300 leading-relaxed">
                  AI Film Society shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of our services.
                </p>
              </section>

              <section>
                <h2 className="font-syne text-2xl text-white mb-4">11. Termination</h2>
                <p className="text-slate-300 leading-relaxed">
                  We may terminate or suspend your account at any time for violations of these terms or for any other reason at our discretion.
                </p>
              </section>

              <section>
                <h2 className="font-syne text-2xl text-white mb-4">12. Changes to Terms</h2>
                <p className="text-slate-300 leading-relaxed">
                  We may update these terms at any time. Continued use of our services after changes constitutes acceptance of the new terms.
                </p>
              </section>

              <section>
                <h2 className="font-syne text-2xl text-white mb-4">13. Contact Us</h2>
                <p className="text-slate-300 leading-relaxed">
                  If you have questions about these Terms of Service, please contact us at{" "}
                  <a href="mailto:sam@aiadstudio.com" className="text-purple-light hover:text-purple-lighter">
                    sam@aiadstudio.com
                  </a>
                </p>
              </section>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
}
